// src/components/partner/PartnerProductsSearchPanel.tsx
//
// PartnerProductsSearchPanel
// - Presentation-only search UI for PartnerProducts page.
// - Delegates all state ownership and applying logic to the parent page.
// - Uses the shared SearchPanel container for:
//   - Search / Reset buttons (single source of truth)
//   - Optional auto-search (debounced) controlled by the page via `autoSearch` prop
//   - Active filter chips (removable, with readable labels)
//
// IMPORTANT (policy):
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel has global defaults via its constants.
//   This component must pass through `autoSearch` only if provided by the page.

import React, { useMemo } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import {
  SearchPanel,
  SearchTextField,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SetSearchValueFn,
  type SearchPanelAction,
} from '@components/common/search';

import type { GetPartnerProductsParams, ProductSaleType } from '@apis/product';
import { PRODUCT_SALE_TYPE_LABELS } from '@utils/productLabels';

type ProductSearchForm = GetPartnerProductsParams;

type Option<T> = {
  label: string;
  value: T;
};

export type PartnerProductsSearchPanelProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;

  // Draft search state (owned by page)
  draftSearch: ProductSearchForm;
  setValue: SetSearchValueFn<ProductSearchForm>;

  // Options + selected values (owned by page)
  countryOptions: Array<Option<number>>;
  saleTypeOptions: Array<Option<ProductSaleType>>;

  selectedCountry: Option<number> | null;
  selectedSaleType: Option<ProductSaleType> | null;

  // Actions (owned by page)
  onSearch: () => void;
  onReset: () => void;

  // Header actions (e.g. export)
  exportDisabled?: boolean;
  exportLoading?: boolean;
  onExport: () => void;

  /**
   * Auto-search configuration provided by the page.
   * If omitted, SearchPanel falls back to its global defaults.
   */
  autoSearch?: { enabled: boolean; delayMs?: number };
};

const PartnerProductsSearchPanel: React.FC<PartnerProductsSearchPanelProps> = ({
  open,
  onOpenChange,

  draftSearch,
  setValue,

  countryOptions,
  saleTypeOptions,
  selectedCountry,
  selectedSaleType,

  onSearch,
  onReset,

  exportDisabled = false,
  exportLoading = false,
  onExport,

  autoSearch,
}) => {
  /* ------------------------------------------------------------------ */
  /* Lookup maps for chips (O(1) label access)                            */
  /* ------------------------------------------------------------------ */

  const countryLabelById = useMemo(() => {
    const map = new Map<number, string>();
    countryOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [countryOptions]);

  const saleTypeLabelByValue = useMemo(() => {
    const map = new Map<ProductSaleType, string>();
    saleTypeOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [saleTypeOptions]);

  /* ------------------------------------------------------------------ */
  /* Active chips configuration                                          */
  /* ------------------------------------------------------------------ */

  const chips: Array<SearchChipSpec<ProductSearchForm>> = useMemo(
    () => [
      {
        key: 'productCode',
        label: 'Product Code',
        getValueText: (s) => String(s.productCode ?? '').trim(),
        clear: ({ setValue }) => setValue('productCode', null),
      },
      {
        key: 'productName',
        label: 'Product Name',
        getValueText: (s) => String(s.productName ?? '').trim(),
        clear: ({ setValue }) => setValue('productName', null),
      },
      {
        key: 'countryId',
        label: 'Country',
        isActive: (s) => typeof s.countryId === 'number',
        getValueText: (s) => {
          const id = s.countryId;
          if (typeof id !== 'number') return '';
          return countryLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('countryId', null),
      },
      {
        key: 'productSaleType',
        label: 'Order Type',
        isActive: (s) => Boolean(s.productSaleType),
        getValueText: (s) => {
          const v = s.productSaleType as ProductSaleType | undefined;
          if (!v) return '';
          return saleTypeLabelByValue.get(v) ?? PRODUCT_SALE_TYPE_LABELS?.[v] ?? String(v);
        },
        clear: ({ setValue }) => setValue('productSaleType', null),
      },
    ],
    [countryLabelById, saleTypeLabelByValue],
  );

  const chipsConfig: ActiveChipsConfig<ProductSearchForm> = useMemo(
    () => ({
      enabled: true,
      search: draftSearch,
      setValue,
      chips,
      maxVisible: 2,
      // Keep behavior consistent with other pages:
      // chip removal should immediately run a search (manual / auto-search is optional).
      onAfterClear: onSearch,
    }),
    [chips, draftSearch, onSearch, setValue],
  );

  /* ------------------------------------------------------------------ */
  /* Actions                                                            */
  /* ------------------------------------------------------------------ */

  const actions: SearchPanelAction[] = useMemo(
    () => [
      {
        key: 'export',
        label: 'Export',
        startIcon: <FileDownloadIcon />,
        variant: 'outlined',
        onClick: onExport,
        disabled: exportDisabled || exportLoading,
      },
    ],
    [exportDisabled, exportLoading, onExport],
  );

  // ✅ 지침 강화: enabled=true인 경우에만 패스스루
  const panelAutoSearch = autoSearch?.enabled === true ? autoSearch : undefined;

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <SearchPanel
      title="Filters"
      open={open}
      onOpenChange={onOpenChange}
      onSearch={onSearch}
      onReset={onReset}
      chipsConfig={chipsConfig}
      // Pass-through only. If undefined, SearchPanel uses global defaults.
      autoSearch={panelAutoSearch}
      actions={actions}
    >
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ minWidth: 0 }}>
        <SearchTextField
          label="Product Code"
          placeholder="Enter product code"
          size="small"
          value={draftSearch.productCode ?? ''}
          onChangeValue={(v) => setValue('productCode', v)}
          onClear={() => setValue('productCode', null)}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />

        <SearchTextField
          label="Product Name"
          placeholder="Enter product name"
          size="small"
          value={draftSearch.productName ?? ''}
          onChangeValue={(v) => setValue('productName', v)}
          onClear={() => setValue('productName', null)}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />

        <Autocomplete<Option<number>, false, false, false>
          disablePortal
          options={countryOptions}
          value={selectedCountry}
          onChange={(_e, v) => setValue('countryId', (v?.value ?? null) as number | null)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          renderInput={(params) => <TextField {...params} label="Country" placeholder="Select country" size="small" />}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />

        <Autocomplete<Option<ProductSaleType>, false, false, false>
          disablePortal
          options={saleTypeOptions}
          value={selectedSaleType}
          onChange={(_e, v) => setValue('productSaleType', (v?.value ?? null) as ProductSaleType | null)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          renderInput={(params) => (
            <TextField {...params} label="Order Type" placeholder="Select order type" size="small" />
          )}
          sx={{ minWidth: 260, flex: '1 1 260px' }}
        />
      </Stack>
    </SearchPanel>
  );
};

export default PartnerProductsSearchPanel;
